import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Button, Space, Tag, Modal, Form, Input, Select, List, Avatar,
  Typography, Row, Col, Statistic, Popconfirm, Tooltip, message, Alert, Empty, Spin,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ReloadOutlined,
  MessageOutlined, TeamOutlined, WechatOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { chatApi, ChatChannel, ChatMessage, ChannelCreatePayload, ChatStats } from '../../api/chat';

const { Title, Text, Paragraph } = Typography;

const CHANNEL_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  general: { label: 'Общий', color: 'blue' },
  district: { label: 'Район', color: 'green' },
  topic: { label: 'Тема', color: 'orange' },
};

const ChatModeration: React.FC = () => {
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ChatStats | null>(null);

  const [selectedChannel, setSelectedChannel] = useState<ChatChannel | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const [channelModalOpen, setChannelModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [channelForm] = Form.useForm();

  const [messageApi, contextHolder] = message.useMessage();

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [channelsRes, statsRes] = await Promise.allSettled([
        chatApi.listChannels(),
        chatApi.stats(),
      ]);
      if (channelsRes.status === 'fulfilled') setChannels(channelsRes.value.data);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChannels(); }, [fetchChannels]);

  const fetchMessages = useCallback(async (channelId: number) => {
    setMessagesLoading(true);
    try {
      const res = await chatApi.listMessages(channelId, { limit: 50 });
      setMessages(res.data);
    } catch {
      messageApi.error('Ошибка загрузки сообщений');
    } finally {
      setMessagesLoading(false);
    }
  }, [messageApi]);

  const selectChannel = (channel: ChatChannel) => {
    setSelectedChannel(channel);
    fetchMessages(channel.id);
  };

  const handleCreateChannel = async () => {
    let values: any;
    try { values = await channelForm.validateFields(); } catch { return; }

    setSubmitting(true);
    try {
      const payload: ChannelCreatePayload = {
        name: values.name,
        channel_type: values.channel_type,
        description: values.description,
      };
      await chatApi.createChannel(payload);
      messageApi.success('Канал создан');
      setChannelModalOpen(false);
      channelForm.resetFields();
      fetchChannels();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка создания');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMessage = async (messageId: number) => {
    try {
      await chatApi.deleteMessage(messageId);
      messageApi.success('Сообщение удалено');
      if (selectedChannel) fetchMessages(selectedChannel.id);
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка удаления');
    }
  };

  return (
    <div>
      {contextHolder}

      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>Городской чат</Title>
          <Text type="secondary">Модерация каналов и сообщений чата</Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { channelForm.resetFields(); channelForm.setFieldsValue({ channel_type: 'general' }); setChannelModalOpen(true); }} style={{ background: 'var(--l-accent)' }}>
          Создать канал
        </Button>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card><Statistic title="Каналов" value={stats?.total_channels ?? channels.length} prefix={<WechatOutlined />} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Сообщений" value={stats?.total_messages ?? 0} valueStyle={{ color: 'var(--l-accent)' }} prefix={<MessageOutlined />} /></Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card style={{ }}><Statistic title="Активных сегодня" value={stats?.active_today ?? 0} valueStyle={{ color: 'var(--l-ok)' }} prefix={<TeamOutlined />} /></Card>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Row gutter={16}>
        {/* Channel List */}
        <Col xs={24} md={8}>
          <Card
            title="Каналы"
            bodyStyle={{ padding: 0, maxHeight: 600, overflowY: 'auto' }}
            extra={<Button icon={<ReloadOutlined />} size="small" onClick={fetchChannels} />}
            loading={loading}
          >
            <List
              dataSource={channels}
              locale={{ emptyText: 'Нет каналов' }}
              renderItem={(channel) => (
                <List.Item
                  onClick={() => selectChannel(channel)}
                  style={{
                    cursor: 'pointer',
                    padding: '12px 16px',
                    background: selectedChannel?.id === channel.id ? '#e6f4ff' : undefined,
                  }}
                >
                  <List.Item.Meta
                    avatar={<Avatar icon={<WechatOutlined />} style={{ background: CHANNEL_TYPE_CONFIG[channel.channel_type]?.color === 'blue' ? 'var(--l-accent)' : CHANNEL_TYPE_CONFIG[channel.channel_type]?.color === 'green' ? 'var(--l-ok)' : 'var(--l-warn)' }} />}
                    title={
                      <Space>
                        <Text strong>{channel.name}</Text>
                        <Tag>
                          {CHANNEL_TYPE_CONFIG[channel.channel_type]?.label || channel.channel_type}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {channel.message_count ?? 0} сообщений
                        </Text>
                        {!channel.is_active && <Tag color="red">Выключен</Tag>}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Messages */}
        <Col xs={24} md={16}>
          <Card
            title={selectedChannel ? `Сообщения: ${selectedChannel.name}` : 'Выберите канал'}
            bodyStyle={{ padding: 0, maxHeight: 600, overflowY: 'auto' }}
            extra={selectedChannel && (
              <Button icon={<ReloadOutlined />} size="small" onClick={() => fetchMessages(selectedChannel.id)} />
            )}
          >
            {!selectedChannel ? (
              <div style={{ padding: 48, textAlign: 'center' }}>
                <Empty description="Выберите канал из списка слева" />
              </div>
            ) : messagesLoading ? (
              <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>
            ) : messages.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center' }}>
                <Empty description="Нет сообщений" />
              </div>
            ) : (
              <List
                dataSource={messages}
                renderItem={(msg) => (
                  <List.Item
                    style={{
                      padding: '12px 16px',
                      opacity: msg.is_deleted ? 0.4 : 1,
                    }}
                    actions={
                      !msg.is_deleted ? [
                        <Popconfirm
                          key="delete"
                          title="Удалить сообщение?"
                          onConfirm={() => handleDeleteMessage(msg.id)}
                          okText="Да"
                          cancelText="Нет"
                        >
                          <Tooltip title="Удалить">
                            <Button size="small" icon={<DeleteOutlined />} danger />
                          </Tooltip>
                        </Popconfirm>,
                      ] : undefined
                    }
                  >
                    <List.Item.Meta
                      avatar={<Avatar>{(msg.user_name || '?')[0]}</Avatar>}
                      title={
                        <Space>
                          <Text strong>{msg.user_name || `#${msg.user_id}`}</Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {dayjs(msg.created_at).format('DD.MM.YYYY HH:mm')}
                          </Text>
                          {msg.is_deleted && <Tag color="red">Удалено</Tag>}
                        </Space>
                      }
                      description={
                        <div>
                          <Paragraph style={{ margin: 0 }}>{msg.text}</Paragraph>
                          {msg.image_url && (
                            <img
                              src={msg.image_url}
                              alt="attachment"
                              style={{ maxWidth: 200, maxHeight: 150, marginTop: 8, borderRadius: 8 }}
                            />
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Create Channel Modal */}
      <Modal title="Создать канал" open={channelModalOpen} onCancel={() => setChannelModalOpen(false)} onOk={handleCreateChannel} confirmLoading={submitting} okText="Создать" cancelText="Отмена" width={500} destroyOnClose>
        <Form form={channelForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Название канала" rules={[{ required: true, message: 'Введите название' }]}>
            <Input placeholder="Общий чат Аксая" />
          </Form.Item>
          <Form.Item name="channel_type" label="Тип" rules={[{ required: true }]}>
            <Select>
              {Object.entries(CHANNEL_TYPE_CONFIG).map(([key, cfg]) => (
                <Select.Option key={key} value={key}>{cfg.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input placeholder="Описание канала" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ChatModeration;
